import { cn } from "components/lib/utils";
import {
  FormField,
  FormSection,
  OutcomeStatus,
  SectionReviewState,
  SectionValueView,
  ValidationStatus,
} from "generated/graphql";
import { Link } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { selectUsers } from "redux/slices/client";
import { selectMyUserId } from "redux/slices/session";
import { stripNull } from "helpers/coll";
import { SectionStatusPicker } from "./SectionStatusPicker";
import { REVIEW_STATE_UI } from "./REVIEW_STATE_UI";
import { AssigneeChipWithPopover } from "./AssigneeChip";
import { useSectionValue } from "./useSectionValue";
import { useContext } from "react";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import { DisplaySectionOutcome, DisplayValidationIssue } from "./DisplaySectionOutcome";

function Section({
  active,
  open,
  formId,
  formPageId,
  sc,
  reviewState,
  onFieldClick,
  caseId,
}: {
  active: boolean;
  formPageId?: number;
  formId: number;
  sc: SectionContainer;
  open: boolean;
  reviewState: SectionReviewState;
  onFieldClick: (fieldId: number, sectionId?: number) => void;
  caseId: number;
}) {
  const users = useAppSelector(selectUsers);
  const myUserId = useAppSelector(selectMyUserId);
  const assigneeId =
    reviewState === SectionReviewState.Validation
      ? sc.sectionValue.validationAssignee
      : sc.sectionValue.outcomeAssignee;
  const assignee = users.find((u) => u.id === assigneeId);
  return (
    <div className={cn("flex flex-col gap-1 p-1", active && "bg-gray-100/40 rounded-md", !open && "p-0")}>
      <div className={cn("flex items-center gap-2", open ? "" : "justify-center")}>
        <Link to={`?formPageId=${formPageId}&sectionId=${sc.formSection?.id}`} title={sc.formSection?.name}>
          <p className={cn("font-bold hover:underline cursor-pointer", sc.disabled ? "text-gray-400" : "")}>
            {open ? sc.formSection?.name : (sc.formSection?.rank ?? 0) + 1}
          </p>
        </Link>
        {open && (
          <>
            <div className="grow" />
            {reviewState !== SectionReviewState.Complete && (
              <AssigneeChipWithPopover
                formId={formId}
                user={assignee}
                isMe={assignee?.id === myUserId}
                isQa={reviewState === SectionReviewState.Validation}
                caseId={caseId}
                sectionId={sc.sectionValue.sectionId}
              />
            )}
          </>
        )}
      </div>
      {open && (
        <div className="flex flex-wrap gap-2 items-center mb-2">
          <>
            <DisplaySectionOutcome className="truncate" outcomeStatus={sc.sectionValue.outcomeStatus} />
            {sc.sectionValue.validationStatus === ValidationStatus.Issue && (
              <>
                <p className={cn(active ? "text-gray-300" : "text-gray-100")}>•</p>
                <DisplayValidationIssue className="truncate" validationStatus={sc.sectionValue.validationStatus} />
              </>
            )}
          </>
        </div>
      )}
      <div className="flex flex-col gap-2 mt-1">
        {sc.formFields.map((field) => (
          <p
            onClick={() => {
              onFieldClick(field.id, stripNull(field.sectionId));
            }}
            className={cn(
              "text-xs hover:underline cursor-pointer",
              sc.disabled ? "text-gray-400" : "",
              open ? "" : "text-center"
            )}
            key={field.id}>
            {field.outlinePrefix}
            {open && ` ${field.name}`}
          </p>
        ))}
      </div>
    </div>
  );
}

interface SectionContainer {
  sectionValue: SectionValueView;
  formSection?: FormSection;
  formFields: FormField[];
  disabled: boolean;
  visible: boolean;
}

export function SectionsByReviewState({
  caseId,
  formId,
  activeSectionId,
  reviewState,
  formSections,
  sectionValues,
  formFields,
  formPageId,
  onFieldClick,
  meMode,
  open,
}: {
  caseId: number;
  formId: number;
  activeSectionId: number | undefined;
  reviewState: SectionReviewState;
  formSections: FormSection[];
  sectionValues: SectionValueView[];
  formFields: FormField[];
  formPageId?: number;
  onFieldClick: (fieldId: number, sectionId?: number) => void;
  meMode: boolean;
  open: boolean;
}) {
  const myUserId = useAppSelector(selectMyUserId);
  const confirmCtx = useContext(ConfirmationDialogContext);
  const sectionValueHook = useSectionValue({ caseId, formId });
  const visibleSectionValues = sectionValues.filter((sv) => formSections.find((fs) => fs.id === sv.sectionId));
  const sectionValuesByState = visibleSectionValues.filter((sv) => sv.reviewState === reviewState);
  const sectionContainers = sectionValuesByState.map((sv) => ({
    sectionValue: sv,
    formSection: formSections.find((fs) => fs.id === sv.sectionId),
    formFields: formFields.filter((ff) => ff.sectionId === sv.sectionId),
    disabled: sv.outcomeStatus === OutcomeStatus.NotApplicable,
    visible: !meMode
      ? true
      : reviewState === SectionReviewState.Complete
        ? true
        : reviewState === SectionReviewState.Review
          ? sv.outcomeAssignee === myUserId
          : sv.validationAssignee === myUserId,
  }));
  const visibleSectionContainers = !meMode ? sectionContainers : sectionContainers.filter((sc) => sc.visible);
  const showState = visibleSectionContainers.length > 0;
  const updateAllSections = async (reviewState: SectionReviewState) => {
    confirmCtx.confirm({
      title: "Update All Sections",
      prompt: (
        <div className="flex flex-col gap-2">
          <p>
            Update the following sections to <strong>{REVIEW_STATE_UI[reviewState].name}</strong>?
          </p>
          {visibleSectionContainers.map((vs) =>
            vs.formSection !== undefined ? (
              <p className="indent-2 font-semibold" key={vs.formSection.id}>
                • {vs.formSection.name}
              </p>
            ) : (
              <></>
            )
          )}
        </div>
      ),
      callback: async () => {
        return await sectionValueHook.updateReviewState(
          visibleSectionContainers.map((vs) => ({
            sectionId: vs.sectionValue.sectionId,
            setReviewState: reviewState,
          }))
        );
      },
    });
  };

  return showState ? (
    <div className="flex flex-col gap-1">
      {open && (
        <SectionStatusPicker
          reviewState={reviewState}
          outcomeEnabled={formSections.reduce((enabled, next) => enabled || next.outcomeEnabled, false)}
          validationEnabled={formSections.reduce((enabled, next) => enabled || next.validationEnabled, false)}
          onChange={(rs) => updateAllSections(rs)}
        />
      )}
      <div
        className={cn("flex flex-col gap-2 rounded-lg border p-1 border-gray-100 divide-y divide-gray-100", [
          REVIEW_STATE_UI[reviewState].color,
          !open ? "p-1" : "",
        ])}>
        {visibleSectionContainers.map((sc, index) => (
          <Section
            active={sc.formSection?.id === activeSectionId}
            key={index}
            sc={sc}
            formPageId={formPageId}
            formId={formId}
            onFieldClick={onFieldClick}
            caseId={caseId}
            open={open}
            reviewState={reviewState}
          />
        ))}
      </div>
      <div className="border-b border-gray-50 pt-3" />
    </div>
  ) : (
    <></>
  );
}

export function UnreviewableFormSections({
  formFields,
  formSections,
  formPageId,
  open,
  onFieldClick,
  activeSectionId,
}: {
  formSections: FormSection[];
  formPageId?: number;
  open: boolean;
  formFields: FormField[];
  onFieldClick: (fieldId: number, sectionId?: number) => void;
  activeSectionId: number | undefined;
}) {
  return (
    <>
      {open && <p className="uppercase text-sm">No Review Required</p>}
      <div className={cn("flex flex-col gap-2 divide-y divide-gray-50 pb-4", !open ? "text-center" : "")}>
        {formSections.map((formSection) => {
          const sectionFormFields = formFields.filter((ff) => ff.sectionId === formSection.id);
          return (
            <div
              key={formSection.id}
              className={cn(
                activeSectionId === formSection.id && "rounded-lg bg-gray-100/40",
                "flex flex-col gap-2 p-2 mt-2",
                !open && "p-0"
              )}>
              <Link to={`?formPageId=${formPageId}&sectionId=${formSection.id}`} title={formSection.name}>
                <p className="font-bold hover:underline cursor-pointer">
                  {open ? formSection?.name : (formSection.rank ?? 0) + 1}
                </p>
              </Link>
              <div className="flex flex-col gap-2">
                {sectionFormFields.map((field) => (
                  <p
                    onClick={() => {
                      onFieldClick(field.id, stripNull(field.sectionId));
                    }}
                    className="text-xs hover:underline cursor-pointer"
                    key={field.id}>
                    {field.outlinePrefix}
                    {open && ` ${field.name}`}
                  </p>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
